import React, { useState } from 'react';
import { EyeOffIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Select } from '../ui/Field';
import { PIPELINE, PIPELINE_OUTCOMES } from '../../types/employer';
import type { Candidate, PipelineOutcome, PipelineStage } from '../../types/employer';
import { candidates as seedCandidates, employerListings } from '../../data/employer';
function tone(stage: PipelineStage | PipelineOutcome) {
  if (stage === 'Hired' || stage === 'Offer') return 'ok' as const;
  if (stage === 'Rejected' || stage === 'Withdrawn') return 'warn' as const;
  return 'brand' as const;
}
export function PipelineBoard() {
  const [candidates, setCandidates] = useState<Candidate[]>(seedCandidates);
  const [listingId, setListingId] = useState('all');
  const visible = candidates.filter(candidate => listingId === 'all' || candidate.listingId === listingId);
  const counts = PIPELINE.map(stage => ({
    stage,
    count: visible.filter(candidate => candidate.stage === stage).length
  }));
  function move(id: string, stage: PipelineStage | PipelineOutcome) {
    setCandidates(current => current.map(candidate => candidate.id === id ? {
      ...candidate,
      stage
    } : candidate));
  }
  return <div className="space-y-4" data-id="element-1566" data-mp="2uoufy56o43e1">
      <div className="flex flex-col gap-3 rounded-card border border-line bg-surface px-5 py-4 sm:flex-row sm:items-center" data-id="element-1567" data-mp="2uo1jbx1fau9m">
        <div className="w-full sm:max-w-xs" data-id="element-1568" data-mp="3iex5oen40x9h">
          <Select aria-label="Filter by listing" value={listingId} onChange={event => setListingId(event.target.value)} data-id="element-1569" data-mp="3mfqsf4otg60s">
            <option value="all" data-id="element-1570" data-mp="38ibzhpzctg42">All listings</option>
            {employerListings.map(listing => <option key={listing.id} value={listing.id} data-id="element-1571" data-mp="3dhf459bf9kbi">
                {listing.title}
              </option>)}
          </Select>
        </div>
        <ul className="flex flex-wrap gap-1.5" data-id="element-1572" data-mp="2216w4sd81gsg">
          {counts.map(item => <li key={item.stage} data-id="element-1573" data-mp="1hyari2o6t2ib">
              <Badge tone={item.count > 0 ? 'brand' : 'neutral'} data-id="element-1574" data-mp="1hxj43f4qub34">
                {item.stage} {item.count}
              </Badge>
            </li>)}
        </ul>
      </div>

      <ul className="space-y-3" data-id="element-1575" data-mp="194fx9polswhg">
        {visible.map(candidate => {
        const listing = employerListings.find(item => item.id === candidate.listingId);
        return <li key={candidate.id} className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-1576" data-mp="z7tbmyoge1ed">
              <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-1577" data-mp="u7f3lgmuqrgc">
                <div className="flex min-w-0 gap-3" data-id="element-1578" data-mp="q6d3yyp0wddu">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[13px] font-semibold text-brand-800" data-id="element-1579" data-mp="q8z9ksscsitf">
                    {candidate.initials}
                  </span>
                  <div className="min-w-0" data-id="element-1580" data-mp="2fltas7fh5n0k">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-1581" data-mp="2fmuka7gue1g6">
                      <p className="text-[14px] font-semibold text-ink" data-id="element-1582" data-mp="2n5u4ugg1hz14">{candidate.displayName}</p>
                      <span className="cz-num text-[12px] text-ink-subtle" data-id="element-1583" data-mp="2s3vn4z5n5ubj">@{candidate.handle}</span>
                      <Badge tone={tone(candidate.stage)} data-id="element-1584" data-mp="2wulu2gtpu58c">{candidate.stage}</Badge>
                    </div>
                    <p className="cz-num mt-1 text-[12px] text-ink-muted" data-id="element-1585" data-mp="31uhwwcqofqmb">
                      {listing?.title} · applied {candidate.appliedOn} · {candidate.university}
                    </p>
                    <dl className="mt-2 space-y-1" data-id="element-1586" data-mp="2951b3xxkb7z2">
                      {candidate.visibleFields.map(field => <div key={field.label} className="flex flex-wrap gap-2 text-[12px]" data-id="element-1587" data-mp="2e3kze4zmovul">
                          <dt className="text-ink-subtle" data-id="element-1588" data-mp="2i7agk9dz4b5l">{field.label}:</dt>
                          <dd className="text-ink-muted" data-id="element-1589" data-mp="3pkc86pvcl74g">{field.value}</dd>
                        </div>)}
                    </dl>
                    <ul className="mt-2 flex flex-wrap gap-1.5" data-id="element-1590" data-mp="3tjl3w09y7f2n">
                      {candidate.skills.map(skill => <li key={skill} data-id="element-1591" data-mp="3n55quh8qnykj">
                          <Badge data-id="element-1592" data-mp="3n67d7hv7g7hk">{skill}</Badge>
                        </li>)}
                    </ul>
                  </div>
                </div>
                <div className="w-full shrink-0 sm:w-52" data-id="element-1593" data-mp="y7i3lddcx36">
                  <Select aria-label={`Stage for ${candidate.displayName}`} value={candidate.stage} onChange={event => move(candidate.id, event.target.value as PipelineStage | PipelineOutcome)} data-id="element-1594" data-mp="wmllq7hv42o">
                    {[...PIPELINE, ...PIPELINE_OUTCOMES].map(stage => <option key={stage} value={stage} data-id="element-1595" data-mp="jp7cx37jp14d">
                        {stage}
                      </option>)}
                  </Select>
                  <Button size="sm" variant="secondary" className="mt-2 w-full" data-id="element-1596" data-mp="tnehsnok7sq0">
                    Message candidate
                  </Button>
                </div>
              </div>

              {candidate.withheldFields.length > 0 && <p className="mt-3 flex items-start gap-2 border-t border-line pt-3 text-[12px] leading-5 text-ink-muted" data-id="element-1597" data-mp="1h7w3mdmyadta">
                  <EyeOffIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-1598" data-mp="18920s5j10r7a" />
                  Withheld by this candidate's career-profile visibility:{' '}
                  {candidate.withheldFields.join(', ')}. Campuzon does not reveal fields a student
                  has not shared, and never exposes academic records to employers.
                </p>}
            </li>;
      })}
      </ul>
    </div>;
}